import { motion } from 'framer-motion';
import { CheckCircle2, Circle, ChevronRight } from 'lucide-react';

/**
 * StepsDisplay - A polished, progress-style layout for numbered steps
 * Renders steps with visual indicators and clean typography
 */
export const StepsDisplay = ({ steps }) => {
  return (
    <div className="steps-container my-4" data-testid="steps-display">
      <div className="relative">
        {/* Vertical progress line */}
        <div className="absolute left-[15px] top-6 bottom-6 w-[2px] bg-gradient-to-b from-amber-400 via-amber-300 to-slate-200" />
        
        {/* Steps */}
        <div className="space-y-3">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.08 }}
              className="flex gap-3 relative"
            >
              {/* Step indicator */}
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-amber-500 flex items-center justify-center shadow-sm z-10">
                <span className="text-sm font-semibold text-slate-900">{index + 1}</span>
              </div>
              
              {/* Step content */}
              <div className="flex-1 pt-1 pb-2">
                <p className="text-slate-700 leading-relaxed">{step}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

/**
 * Detects if content contains a numbered list that should be rendered as steps
 * Returns { hasSteps, beforeSteps, steps, afterSteps }
 */
export const parseStepsFromContent = (content) => {
  const lines = content.split('\n');
  const result = {
    hasSteps: false,
    beforeSteps: [],
    steps: [],
    afterSteps: []
  };
  
  let inSteps = false;
  let stepsEnded = false;
  
  for (const line of lines) {
    const stepMatch = line.match(/^(\d+)\.\s+(.+)$/);
    
    if (stepMatch && !stepsEnded) {
      // Found a numbered step
      if (!inSteps) {
        inSteps = true;
        result.hasSteps = true;
      }
      result.steps.push(stepMatch[2].trim());
    } else if (inSteps && line.trim() === '') {
      // Empty line while in steps - might be a break between steps or end
      continue;
    } else if (inSteps && !stepMatch) {
      // Non-step line after steps started - steps have ended
      stepsEnded = true;
      inSteps = false;
      if (line.trim()) {
        result.afterSteps.push(line);
      }
    } else if (!inSteps && !stepsEnded) {
      // Before steps
      if (line.trim()) {
        result.beforeSteps.push(line);
      }
    } else {
      // After steps
      if (line.trim()) {
        result.afterSteps.push(line);
      }
    }
  }
  
  // Only treat as steps if we have 2 or more consecutive numbered items
  if (result.steps.length < 2) {
    result.hasSteps = false;
    result.steps = [];
  }
  
  return result;
};

export default StepsDisplay;
